import React, { useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/function-management.css";
import { useAppContext } from "../context/AppContext";
import { getFunctionById } from "../services/functionService";
import ColorPicker from "../components/ui/ColorPicker";
import RichTextEditor from "../components/ui/RichTextEditor";
import SideTabs from "../components/layout/SideTabs";
import { toast } from "../utils/toast";
import { useLocation, useParams } from "react-router-dom";

const FunctionDetailPage = () => {
  const { functionId, setFunctionId, setActiveSection, setFunctionFormMode } =
    useAppContext();
  const { functionId: functionIdParam } = useParams();
  const location = useLocation();
  const routeFunctionId = useMemo(() => {
    if (functionIdParam != null && functionIdParam !== "") {
      try {
        return decodeURIComponent(functionIdParam);
      } catch {
        return functionIdParam;
      }
    }
    const pathname = String(location?.pathname || "");
    const m = pathname.match(/^\/functions\/([^/]+)$/i);
    if (!m?.[1]) return null;
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  }, [functionIdParam, location?.pathname]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // Recover function id from localStorage on hard refresh
        let fid = routeFunctionId || functionId;
        if (routeFunctionId) {
          try {
            localStorage.setItem("activeFunctionId", String(routeFunctionId));
          } catch {}
          setFunctionId(routeFunctionId);
        }
        if (!fid) {
          try {
            const stored = localStorage.getItem("activeFunctionId");
            if (stored) {
              fid = stored;
              setFunctionId(stored);
            }
          } catch {}
        }
        const f = fid ? await getFunctionById(fid) : null;
        setData(f);
      } catch (e) {
        toast.error(e?.message || "Failed to load function");
      } finally {
        setLoading(false);
      }
    })();
  }, [functionId, routeFunctionId, setFunctionId]);

  const goBack = () => {
    try {
      localStorage.removeItem("activeFunctionId");
    } catch {}
    setActiveSection("function-management");
  };
  const goEdit = () => {
    setFunctionFormMode("edit");
    const targetId = routeFunctionId || functionId || data?.function_id;
    if (!targetId) {
      toast.error("Unable to determine function ID");
      return;
    }
    setActiveSection("function-edit", { functionId: targetId });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Function Details</h2>
            <p className="page-subtitle">
              {data?.name ?
                `Viewing: ${data.name}`
              : "Read-only view of a function"}
              {data?.function_code || data?.functionCode ?
                ` (${data.function_code || data.functionCode})`
              : ""}
            </p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={goBack}>
              ← Back
            </button>
            <button className="primary-btn" onClick={goEdit}>
              Edit
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ?
          <div className="no-results">Loading...</div>
        : !data ?
          <div className="no-results">Function not found</div>
        : <SideTabs
            defaultActiveId="basic"
            tabs={[
              {
                id: "basic",
                label: "Basic Details",
                content: (
                  <div className="role-card">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Function Name</label>
                        <input value={data.name || ""} disabled />
                      </div>

                      <ColorPicker
                        label="Background Color"
                        value={
                          data.background_color ||
                          data.backgroundColor ||
                          "#A3A3A3"
                        }
                        disabled
                        onChange={() => {}}
                      />

                      <div className="form-group">
                        <label>Function Code</label>
                        <input
                          value={data.function_code || data.functionCode || ""}
                          disabled
                        />
                      </div>

                      <div className="form-group">
                        <label>Select Company</label>
                        <select
                          value={
                            data.company_id || data.company?.company_id || ""
                          }
                          disabled
                        >
                          <option value={data.company_id || ""}>
                            {data.company_name || data.company?.name || "-"}
                          </option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Parent Function</label>
                        <select
                          value={
                            data.parent_id ||
                            data.parent_function_id ||
                            data.Function?.function_id ||
                            data.parentFunction?.function_id ||
                            ""
                          }
                          disabled
                        >
                          {(
                            data.parent_id ||
                            data.parent_function_id ||
                            data.Function?.function_id ||
                            data.parentFunction?.function_id
                          ) ?
                            <option
                              value={
                                data.parent_id ||
                                data.parent_function_id ||
                                data.Function?.function_id ||
                                data.parentFunction?.function_id ||
                                ""
                              }
                            >
                              {data.parent_name ||
                                data.Function?.name ||
                                data.parentFunction?.name ||
                                "-"}
                            </option>
                          : <option value="">None</option>}
                        </select>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: "description",
                label: "Description",
                content: (
                  <div className="role-card">
                    <RichTextEditor
                      label="Function Overview"
                      value={data.description || data.overview || ""}
                      readOnly
                      height={260}
                    />
                  </div>
                ),
              },
            ]}
          />
        }
      </div>
    </div>
  );
};

export default FunctionDetailPage;
